import React, { useRef, useEffect, useMemo } from "react";
import { Router, Route, Switch, useLocation, matchPath } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box } from "@mui/material";
import { browserHistory } from "rdx/configureStore";
import { selectors } from "rdx";
import SettingsClient from "util/SettingsClient";
import ReactGA from "react-ga";
import WindowDimensionProvider from "containers/router/WindowDimensionProvider";
import ReferrerInfoBox from "components/HomepageComponents/ReferrerInfoBox";

import routes from "containers/router/routes";

import loadable from "@loadable/component";
import { SettingsContext } from "context/SettingsContext";

const AmbassadorRouter = loadable(async () => {
  const r = await import("./routers/Ambassador");
  return r;
});

const GeneralRouter = loadable(async () => {
  const r = await import("./routers/General");
  return r;
});

const PlatformRouter = loadable(async () => {
  const r = await import("./routers/Platform");
  return r;
});

const PersonalRouter = loadable(async () => {
  const r = await import("./routers/Personal");
  return r;
});

const { generalRoutes, ambassadorRoutes, personalRoutes, platformRoutes, home } = routes;

const getMatchingPaths = (r) => {
  return r.map(({ path }) => path);
};

const RootRouter = () => {
  const settings = new SettingsClient();
  const location = useLocation();
  const gaRef = useRef();
  const pageReferrer = useSelector(selectors.selectPageReferrer);
  const staticPaths = useMemo(() => [...getMatchingPaths(personalRoutes), home.path], []);
  const isStaticPage = staticPaths.some((path) => matchPath(location.pathname, { path }));

  useEffect(() => {
    const debug =
      process.env.REACT_APP_ENABLE_GOOGLE_ANALYTICS_LOGGING === "true" && process.env.REACT_APP_ENV !== "development";
    if (settings.data.google_analytics_code && !gaRef.current) {
      ReactGA.initialize(settings.data.google_analytics_code, {
        debug,
      });
      gaRef.current = ReactGA.ga;
    }
  }, [settings.data.google_analytics_code]);

  useEffect(() => {
    if (gaRef.current) {
      ReactGA.pageview(location.pathname + location.search + location.hash);
    }
  }, [location.pathname, location.search, location.hash]);

  const handleRedirects = () => {
    if (location.pathname === "/ourstory") {
      window.location.href = process.env.REACT_APP_WEFUNDER_URL;
      return true;
    }
    return false;
  };

  if (handleRedirects()) {
    return null;
  }

  return (
    <Router history={browserHistory}>
      <WindowDimensionProvider>
        <SettingsContext.Provider value={settings.data}>
          {pageReferrer && isStaticPage && (
            <Box sx={{ display: "block", position: "fixed", top: 10, right: 16, zIndex: 1200 }}>
              <ReferrerInfoBox user={pageReferrer} />
            </Box>
          )}
          <Switch>
            <Route exact path={getMatchingPaths(generalRoutes)} component={GeneralRouter} />
            <Route exact path={getMatchingPaths(personalRoutes)} component={PersonalRouter} />
            <Route exact path={getMatchingPaths(ambassadorRoutes)} component={AmbassadorRouter} />
            <Route exact path={getMatchingPaths(platformRoutes)} component={PlatformRouter} />
            <Route {...home} />
          </Switch>
        </SettingsContext.Provider>
      </WindowDimensionProvider>
    </Router>
  );
};

export default RootRouter;
